import type { AppDispatch, RootState } from "../../../store.ts"
import {
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  fetchFinancialTransactionItemsFilterOptions
} from "./project-finance/project-finance.thunks.ts"
import { fetchInvoiceSnapshot } from "./project-invoices/project-invoices.thunks.ts"
import { fetchProjectMilestoneDependencies } from "./project-milestones/project-milestone-dependencies.thunks.ts"
import {
  fetchProjectMilestoneSnapshot,
  fetchProjectMilestonesFilterOptions
} from "./project-milestones/project-milestones.thunks.ts"
import {
  projectPageReadTaken,
  projectPageReadsOffered
} from "./project.slice.ts"
import { fetchProject } from "./project.thunks.ts"
import { type ProjectPageRead } from "./project.types.ts"

/** Everything the project page can read from the project id alone. */
export type ProjectOpenRead =
  | ProjectPageRead
  | "financialSnapshot"
  | "financialAlerts"

const START = {
  milestoneSnapshot: (id: string) => fetchProjectMilestoneSnapshot(id),
  invoiceSnapshot: (id: string) => fetchInvoiceSnapshot(id),
  milestoneDependencies: (id: string) => fetchProjectMilestoneDependencies(id),
  milestonesFilterOptions: (id: string) =>
    fetchProjectMilestonesFilterOptions({ project_id: id }),
  financeFilterOptions: (id: string) =>
    fetchFinancialTransactionItemsFilterOptions({ project_id: id }),
  financialSnapshot: (id: string) => fetchFinancialSnapshot(id),
  financialAlerts: (id: string) => fetchFinancialAlerts(id)
} satisfies Record<ProjectOpenRead, (projectId: string) => unknown>

/** How long a finished read the page open started may still be handed over. */
export const OFFERED_READ_FRESH_MS = 30_000

const PAGE_READS: ProjectPageRead[] = [
  "milestoneSnapshot",
  "invoiceSnapshot",
  "milestoneDependencies",
  "milestonesFilterOptions",
  "financeFilterOptions"
]

const alreadyAsked = (
  read: ProjectOpenRead,
  project: RootState["project"],
  projectId: string
) => {
  if (read === "financialSnapshot")
    return project.financialSnapshotRequestedFor === projectId
  if (read === "financialAlerts")
    return project.financialAlertsRequestedFor === projectId
  return project.pageReads?.[read]?.projectId === projectId
}

/**
 * Opens a project. The details are read first and alone: read beside them,
 * the other reads slowed the details enough that the header showed later.
 * The moment they arrive, every read the opening screens need from the id
 * starts, without waiting for those screens to load and mount; the screens
 * then take these reads instead of starting their own.
 */
export const openProjectPage =
  (projectId: string, reads: ProjectOpenRead[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const details = dispatch(fetchProject(projectId))
    void details.then(() => {
      const project = getState().project
      // A project that failed to open, or another opened since, reads nothing.
      if (project.projectRequestId !== details.requestId) return
      if (project.project?.id !== projectId) return
      const started = reads.filter((read) => {
        if (alreadyAsked(read, getState().project, projectId)) return false
        void dispatch(START[read](projectId) as never)
        return true
      })
      dispatch(
        projectPageReadsOffered({
          projectId,
          at: Date.now(),
          reads: started.filter((read): read is ProjectPageRead =>
            PAGE_READS.includes(read as ProjectPageRead)
          )
        })
      )
    })
    return details
  }

// The first screen to want a read the page open started takes it, whatever its
// outcome, while it is out or just finished; a read still out is waited for;
// anything else is read again, as a screen opened later always did.
const loadPageRead =
  (read: ProjectPageRead, projectId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const entry = getState().project.pageReads?.[read]
    if (entry?.projectId === projectId) {
      const fresh =
        entry.status === "pending" ||
        Date.now() - (entry.offeredAt ?? 0) < OFFERED_READ_FRESH_MS
      if (entry.offered && fresh) {
        dispatch(projectPageReadTaken(read))
        return
      }
      if (entry.status === "pending") return
    }
    return dispatch(START[read](projectId) as never)
  }

export const loadMilestoneSnapshot = (projectId: string) =>
  loadPageRead("milestoneSnapshot", projectId)

export const loadInvoiceSnapshot = (projectId: string) =>
  loadPageRead("invoiceSnapshot", projectId)

export const loadMilestoneDependencies = (projectId: string) =>
  loadPageRead("milestoneDependencies", projectId)

export const loadMilestonesFilterOptions = (projectId: string) =>
  loadPageRead("milestonesFilterOptions", projectId)

export const loadFinanceFilterOptions = (projectId: string) =>
  loadPageRead("financeFilterOptions", projectId)
